import * as Sentry from '@sentry/nextjs';
import axios, { AxiosError } from 'axios';
import { toast } from 'react-toastify';

let lastToastTime = 0;
const THROTTLE_DURATION = 3000;

const TOAST_MESSAGES = {
  OFFLINE: '인터넷 연결이 불안정합니다. 연결을 확인해주세요.',
  NETWORK_ERROR: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
  UNKNOWN_ERROR: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  SERVER_ERROR: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
};

interface SentryErrorOptions {
  level?: Sentry.SeverityLevel;
  errorType?: string;
}

const sendSentryError = (error: unknown, options: SentryErrorOptions = {}) => {
  const { level = 'error', errorType } = options;

  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (errorType) {
      scope.setTag('error_type', errorType);
    }

    if (axios.isAxiosError(error)) {
      const contexts = buildAxiosErrorContexts(error);
      Object.entries(contexts).forEach(([key, value]) => {
        scope.setContext(key, value);
      });

      // AxiosError는 스택 트레이스가 동일하여 상태 코드가 달라도 하나로 묶이므로,
      // status를 fingerprint에 포함하여 상태 코드별로 이슈를 분리한다.
      if (error.response) {
        scope.setFingerprint(['{{ default }}', String(error.response.status)]);
      }
    }

    Sentry.captureException(error);
  });
};

const showThrottledToast = (message: string) => {
  const now = Date.now();
  if (now - lastToastTime > THROTTLE_DURATION) {
    lastToastTime = now;
    toast.error(message);
  }
};

const buildAxiosErrorContexts = (
  error: AxiosError,
): Record<string, Record<string, unknown>> => {
  const contexts: Record<string, Record<string, unknown>> = {};
  const { config, response } = error;

  if (config) {
    contexts['API Request Detail'] = {
      method: config.method,
      baseURL: config.baseURL,
      url: config.url,
    };
  }

  if (response) {
    const responseData = response.data as Record<string, unknown>;
    contexts['API Response Detail'] = {
      status: response.status,
      statusText: response.statusText,
      message: responseData?.message,
      code: responseData?.code,
    };
  }

  return contexts;
};

const handleNoResponseError = (error: AxiosError) => {
  if (error.code === 'ERR_CANCELED') {
    return;
  }

  if (!navigator.onLine) {
    console.warn('클라이언트 오프라인 상태:', error.message);
    showThrottledToast(TOAST_MESSAGES.OFFLINE);
    return;
  }

  if (error.code === 'ERR_NETWORK') {
    console.warn('네트워크 에러:', error.message);
    // 네트워크 에러 세밀하게 구분할 수 있는 방법 찾아서 적용 고려 필요. 사용자에게 과하게 노출 되는 경우 대비해 임시 주석 처리함
    // showThrottledToast(TOAST_MESSAGES.NETWORK_ERROR);
    return;
  }

  console.error('예상하지 못한 에러 (response 없음):', error.message);
  showThrottledToast(TOAST_MESSAGES.UNKNOWN_ERROR);
  sendSentryError(error, { errorType: error.code || 'unknown' });
};

const handleResponseError = (error: AxiosError) => {
  const { response } = error;
  const responseData = response?.data as Record<string, unknown>;
  const isServerError = response?.status >= 500;

  if (isServerError) {
    showThrottledToast(TOAST_MESSAGES.SERVER_ERROR);
  }

  if (
    // 빈번하게 발생하면서, 딱히 리포트될 가치가 없는 에러이므로 Sentry에 전달하지 않음.
    // TODO : 향후 필요 시 프론트엔드에서 문제 제출 중복 방지 로직을 구현하는 방향으로 개선 고려 필요
    response?.status === 422 &&
    responseData?.code === 'PROBLEM_ALREADY_SUBMITTED'
  ) {
    return;
  }

  sendSentryError(error, {
    level: isServerError ? 'error' : 'warning',
  });
};

export const handleError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('Axios 외부 에러:', message);
    sendSentryError(error, { errorType: 'non_axios_error' });
    return;
  }

  if (!error.response) {
    handleNoResponseError(error);
    return;
  }

  handleResponseError(error);
};

export const safeApiCall = async <T>(
  apiFunc: () => Promise<T>,
): Promise<{ data: T | null; error: unknown }> => {
  try {
    const data = await apiFunc();
    return { data, error: null };
  } catch (error) {
    handleError(error);
    return { data: null, error };
  }
};
